import { useSuspenseQuery } from "@apollo/client";
import { PrimaryFullScreenSpinner } from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import {
  ActionsCard,
  AnnotationsCard,
  DetailsCard,
  type LedgerTransactionAnnotation,
  type LedgerTransactionFundMovement,
  type LedgerTransactionTag,
  LinkedEntityCard,
  TagsCard,
} from "~/components/ledger-transaction-detail/cards";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/ledger/transactions_/$transactionId/",
)({
  head: () => opsHead("Ledger Transaction"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const ledgerTransactionDetailQuery = graphql(`
  query AdminLedgerTransactionDetail($transactionId: ID!) {
    ledgerTransaction(id: $transactionId) {
      ...LedgerTransactionOpsDetails
    }
  }
`);

const mapTags = (
  tags?: ({ id: string; key: string; value?: string | null } | null)[] | null,
): LedgerTransactionTag[] =>
  tags?.filter((tag): tag is LedgerTransactionTag => Boolean(tag)) ?? [];

const mapAnnotations = (
  annotations?: (LedgerTransactionAnnotation | null)[] | null,
): LedgerTransactionAnnotation[] =>
  annotations?.filter((annotation): annotation is LedgerTransactionAnnotation =>
    Boolean(annotation),
  ) ?? [];

const mapFundMovement = (
  fundMovement:
    | {
        __typename: "Deposit";
        id: string;
        shortRef?: string | null;
      }
    | {
        __typename: "Transfer";
        id: string;
        shortRef?: string | null;
      }
    | {
        __typename: "EburyFXTrade";
        id: string;
        shortRef?: string | null;
      }
    | {
        __typename: "FundUnitOrder";
        id: string;
        shortRef?: string | null;
      }
    | null
    | undefined,
): LedgerTransactionFundMovement | null => {
  if (!fundMovement) {
    return null;
  }

  switch (fundMovement.__typename) {
    case "Deposit":
      return {
        kind: "deposit",
        id: fundMovement.id,
        shortRef: fundMovement.shortRef ?? null,
      };
    case "Transfer":
      return {
        kind: "transfer",
        id: fundMovement.id,
        shortRef: fundMovement.shortRef ?? null,
      };
    case "EburyFXTrade":
      return {
        kind: "fx_trade",
        id: fundMovement.id,
        shortRef: fundMovement.shortRef ?? null,
      };
    case "FundUnitOrder":
      return {
        kind: "fund_unit_order",
        id: fundMovement.id,
        shortRef: fundMovement.shortRef ?? null,
      };
    default:
      return null;
  }
};

function View() {
  const { transactionId } = Route.useParams();

  const { data } = useSuspenseQuery(ledgerTransactionDetailQuery, {
    variables: { transactionId },
  });

  const ledgerTxn = data?.ledgerTransaction;
  if (!ledgerTxn) {
    throw new NotFoundError();
  }

  useOpsTitle(`Ledger Transaction #${ledgerTxn.shortRef}`);

  const tags = mapTags(ledgerTxn.tags);
  const annotations = mapAnnotations(ledgerTxn.annotations);
  const fundMovement = mapFundMovement(ledgerTxn.fundMovement);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`Ledger Transaction #${ledgerTxn.shortRef}`}
        subtitle={<div className="font-mono">{ledgerTxn.id}</div>}
      />
      <div className="flex flex-row gap-4">
        <div className="flex basis-2/3 flex-col gap-4">
          <DetailsCard
            id={ledgerTxn.id}
            status={ledgerTxn.status}
            effectiveAt={ledgerTxn.effectiveAt}
            postedAt={ledgerTxn.postedAt ?? null}
            reference={ledgerTxn.reference ?? null}
            description={ledgerTxn.description ?? null}
          />
        </div>
        <div className="flex basis-1/3 flex-col gap-4">
          <AnnotationsCard
            transactionId={ledgerTxn.id}
            annotations={annotations}
          />
          <LinkedEntityCard
            transactionId={ledgerTxn.id}
            fundMovement={fundMovement}
          />
          <TagsCard transactionId={ledgerTxn.id} tags={tags} />
          <ActionsCard transactionId={ledgerTxn.id} status={ledgerTxn.status} />
        </div>
      </div>
    </AdminPageContainer>
  );
}
